import { BaseLayout } from '@kurotetsu/individual/layouts';
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  useRouteError
} from 'react-router-dom';
import PrivateRoute from './PrivateRoute';

const Dashboard = lazy(
  () => import('@kurotetsu/individual/views/Dashboard/List')
);
const DashboardContentDetail = lazy(
  () => import('@kurotetsu/share/views/Dashboard/Detail')
);
const Login = lazy(() => import('@kurotetsu/individual/views/Login'));
const Register = lazy(
  () => import('@kurotetsu/individual/views/User/Register')
);
const AccountCreate = lazy(
  () => import('@kurotetsu/individual/views/IndividualGroup/AccountCreate')
);
const ResetPassword = lazy(
  () => import('@kurotetsu/individual/views/ResetPassword')
);
const ChangePassword = lazy(
  () => import('@kurotetsu/individual/views/ChangePassword')
);
const ResetPasswordConfirm = lazy(
  () => import('@kurotetsu/share/views/Password/ResetConfirm')
);

const IndividualGroupList = lazy(
  () => import('@kurotetsu/individual/views/IndividualGroup/List')
);
const IndividualGroupDetail = lazy(
  () => import('@kurotetsu/individual/views/IndividualGroup/Detail')
);
const IndividualGroupUpdate = lazy(
  () => import('@kurotetsu/individual/views/IndividualGroup/Update')
);
const SearchAllotmentsPeriodSelection = lazy(
  () => import('@kurotetsu/individual/views/SearchAllotments/PeriodSelection')
);

const SearchAllotments = lazy(
  () => import('@kurotetsu/individual/views/SearchAllotments')
);
const SearchAllotmentsByDate = lazy(
  () => import('@kurotetsu/individual/views/SearchAllotments/ByDate')
);

const RestaurantBookingList = lazy(
  () => import('@kurotetsu/individual/views/RestaurantBooking/List')
);
const RestaurantBookingCreate = lazy(
  () => import('@kurotetsu/individual/views/RestaurantBooking/Create')
);
const RestaurantBookingConfirm = lazy(
  () => import('@kurotetsu/individual/views/RestaurantBooking/Confirm')
);
const RestaurantBookingDetail = lazy(
  () => import('@kurotetsu/individual/views/RestaurantBooking/Detail')
);
const RestaurantBookingUpdate = lazy(
  () => import('@kurotetsu/individual/views/RestaurantBooking/Update')
);
const RestaurantBookingUpdateConfirm = lazy(
  () => import('@kurotetsu/individual/views/RestaurantBooking/UpdateConfirm')
);
const RestaurantBookingCancel = lazy(
  () => import('@kurotetsu/individual/views/RestaurantBooking/Cancel')
);
const RestaurantBookingHistoryDetail = lazy(
  () => import('@kurotetsu/individual/views/RestaurantBooking/HistoryDetail')
);
const SalesManagerList = lazy(
  () => import('@kurotetsu/individual/views/SalesManager/List')
);
const SalesManagerDetail = lazy(
  () => import('@kurotetsu/individual/views/SalesManager/Detail')
);
const SalesManagerTicketVouchPayment = lazy(
  () => import('@kurotetsu/individual/views/SalesManager/TicketVouchPayment')
);
const SalesManagerNonBoardingCertificateIssue = lazy(
  () =>
    import('@kurotetsu/individual/views/SalesManager/NonBoardingCertificateIssue')
);
const SalesManagerSalesAdjustment = lazy(
  () => import('@kurotetsu/individual/views/SalesManager/SalesAdjustment')
);
const SalesManagerCreditCardPaymentResult = lazy(
  () =>
    import('@kurotetsu/individual/views/SalesManager/CreditCardPaymentResult')
);
const BookingCreate = lazy(
  () => import('@kurotetsu/individual/views/Booking/Create')
);
const BookingUpdate = lazy(
  () => import('@kurotetsu/individual/views/Booking/Update')
);
const BookingConfirm = lazy(
  () => import('@kurotetsu/individual/views/Booking/Confirm')
);
const BookingDetail = lazy(
  () => import('@kurotetsu/individual/views/Booking/Detail')
);
const BookingHistoryDetail = lazy(
  () => import('@kurotetsu/individual/views/Booking/HistoryDetail')
);
const BookingCancel = lazy(
  () => import('@kurotetsu/individual/views/Booking/Cancel')
);
const NotificationList = lazy(
  () => import('@kurotetsu/individual/views/NotificationList')
);
const BookingSearch = lazy(
  () => import('@kurotetsu/individual/views/BookingSearch')
);

function RootErrorBoundary() {
  const error = useRouteError();
  const errorMessage = error instanceof Error ? error.message : '';
  if (errorMessage.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => (
  <Suspense fallback="Loading...">{view}</Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route element={<PrivateRoute />}>
        <Route path="/" element={<BaseLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={lazyView(<Dashboard />)} />
          <Route
            path="dashboard/contents/:contentId"
            element={lazyView(<DashboardContentDetail />)}
          />
          <Route
            path="change-password"
            element={lazyView(<ChangePassword />)}
          />
          <Route path="individual-groups">
            <Route index element={lazyView(<IndividualGroupList />)} />
            <Route
              path=":detailId"
              element={lazyView(<IndividualGroupDetail />)}
            />
            <Route
              path=":detailId/update"
              element={lazyView(<IndividualGroupUpdate />)}
            />
          </Route>

          {/* 座席照会 */}
          <Route path="search-allotments">
            <Route
              path="period-selection"
              element={lazyView(<SearchAllotmentsPeriodSelection />)}
            />
            <Route index element={lazyView(<SearchAllotments />)} />
            <Route path=":date">
              <Route index element={lazyView(<SearchAllotmentsByDate />)} />
            </Route>
          </Route>
          {/* レストラン食事予約 */}
          <Route path="restaurant-bookings">
            <Route index element={lazyView(<RestaurantBookingList />)} />
            <Route
              path="create"
              element={lazyView(<RestaurantBookingCreate />)}
            />
            <Route
              path="confirm/:holdToken"
              element={lazyView(<RestaurantBookingConfirm />)}
            />
            <Route path=":restaurantBookingId">
              <Route index element={lazyView(<RestaurantBookingDetail />)} />
              <Route
                path="update"
                element={lazyView(<RestaurantBookingUpdate />)}
              />
              <Route
                path="update-confirm/:holdToken"
                element={lazyView(<RestaurantBookingUpdateConfirm />)}
              />
              <Route
                path="cancel"
                element={lazyView(<RestaurantBookingCancel />)}
              />
              <Route
                path="histories/:historyId"
                element={lazyView(<RestaurantBookingHistoryDetail />)}
              />
            </Route>
          </Route>
          <Route path="bookings">
            <Route path="create" element={lazyView(<BookingCreate />)} />
            <Route path="confirm" element={lazyView(<BookingConfirm />)} />
            <Route
              path=":groupBookingNo"
              element={lazyView(<BookingDetail />)}
            />
            <Route
              path=":groupBookingNo/update"
              element={lazyView(<BookingUpdate />)}
            />
            <Route
              path=":groupBookingNo/copy"
              element={lazyView(<BookingCreate />)}
            />
            <Route
              path=":groupBookingNo/histories/:historyId"
              element={lazyView(<BookingHistoryDetail />)}
            />
            <Route
              path=":groupBookingNo/cancel"
              element={lazyView(<BookingCancel />)}
            />
          </Route>
          <Route path="sales-managers">
            <Route index element={lazyView(<SalesManagerList />)} />
            <Route
              path=":groupBookingId"
              element={lazyView(<SalesManagerDetail />)}
            />
            <Route
              path=":groupBookingId/ticket-vouch-payment"
              element={lazyView(<SalesManagerTicketVouchPayment />)}
            />
            <Route
              path=":groupBookingId/sales-adjustment"
              element={lazyView(<SalesManagerSalesAdjustment />)}
            />
            <Route
              path=":groupBookingId/non-boarding-certificate-issue"
              element={lazyView(<SalesManagerNonBoardingCertificateIssue />)}
            />
            <Route
              path=":groupBookingId/credit-card-payment/result"
              element={lazyView(<SalesManagerCreditCardPaymentResult />)}
            />
          </Route>
          <Route
            path="payment/result"
            element={lazyView(<SalesManagerCreditCardPaymentResult />)}
          />
          <Route
            path="notifications"
            element={lazyView(<NotificationList />)}
          />
          <Route
            path="booking-search"
            element={lazyView(
              <BookingSearch title="layouts.sidebar.groupManager.bookingSearch" />
            )}
          />
        </Route>
      </Route>
      <Route path="/login" element={lazyView(<Login />)} />
      <Route path="/user-register" element={lazyView(<Register />)} />
      <Route path="/account-create" element={lazyView(<AccountCreate />)} />
      <Route path="/reset-password" element={lazyView(<ResetPassword />)} />
      <Route
        path="/reset-password-confirm"
        element={lazyView(
          <ResetPasswordConfirm
            loginPath="/login"
            resetPasswordPath="/reset-password"
          />
        )}
      />
    </Route>
  )
);

export default router;
